import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { cancelledWithAccess, planLabel } from "../lib/subscription";
import { NAV } from "../constants/testIds";
import { startPushScheduler, registerServiceWorker } from "../lib/pushScheduler";
import { initOfflineSync } from "../lib/offlineQueue";
import {
  LayoutDashboard,
  Dumbbell,
  UtensilsCrossed,
  Apple,
  MessageSquare,
  TrendingUp,
  CreditCard,
  Shield,
  LogOut,
  Flame,
  Bell,
  Ruler,
  Share2,
  Award,
  BookOpen,
  Send,
  PlayCircle,
  Menu,
  X,
  Settings as SettingsIcon,
} from "lucide-react";

const Item = ({ to, icon: Icon, label, testid, badge, onClick }) => (
  <NavLink
    to={to}
    data-testid={testid}
    onClick={onClick}
    className={({ isActive }) =>
      `nav-item ${isActive ? "nav-item-active" : ""}`
    }
  >
    <Icon className="h-4 w-4" />
    <span className="flex-1">{label}</span>
    {badge > 0 && (
      <span className="bg-blaze text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm leading-none">
        {badge > 99 ? "99+" : badge}
      </span>
    )}
  </NavLink>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  // Single role-aware nav definition drives the desktop sidebar AND the
  // mobile "More" drawer, so every feature is reachable on phones too.
  const navItems = user?.is_coach
    ? [
        { to: "/coach-dashboard", icon: Award, label: "My Clients", testid: "nav-coach-dashboard" },
        { to: "/exercises", icon: BookOpen, label: "Exercises", testid: "nav-exercises-coach" },
        { to: "/videos", icon: PlayCircle, label: "Videos", testid: "nav-videos-coach" },
        { to: "/notifications", icon: Bell, label: "Notifications", testid: NAV.notifications, badge: unread },
        { to: "/pricing", icon: CreditCard, label: "Pricing", testid: NAV.pricing },
        { to: "/settings", icon: SettingsIcon, label: "Settings", testid: "nav-settings-coach" },
        ...(user?.is_admin ? [{ to: "/admin", icon: Shield, label: "Admin", testid: NAV.admin }] : []),
      ]
    : [
        { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", testid: NAV.dashboard },
        { to: "/workouts", icon: Dumbbell, label: "Workouts", testid: NAV.workouts },
        { to: "/exercises", icon: BookOpen, label: "Exercises", testid: "nav-exercises" },
        { to: "/videos", icon: PlayCircle, label: "Videos", testid: "nav-videos" },
        { to: "/meals", icon: UtensilsCrossed, label: "Meal Plan", testid: NAV.meals },
        { to: "/food", icon: Apple, label: "Food Log", testid: "nav-food" },
        { to: "/progress", icon: TrendingUp, label: "Progress", testid: NAV.progress },
        { to: "/measurements", icon: Ruler, label: "Measurements", testid: NAV.measurements },
        { to: "/share", icon: Share2, label: "Share", testid: "nav-share" },
        { to: "/notifications", icon: Bell, label: "Notifications", testid: NAV.notifications, badge: unread },
        { to: "/pricing", icon: CreditCard, label: "Pricing", testid: NAV.pricing },
        { to: "/settings", icon: SettingsIcon, label: "Settings", testid: "nav-settings" },
        ...(user?.is_admin ? [{ to: "/admin", icon: Shield, label: "Admin", testid: NAV.admin }] : []),
      ];

  // Quick tabs on the mobile bottom bar (the drawer covers the rest).
  const quickTabs = user?.is_coach
    ? [
        { to: "/coach-dashboard", icon: Award, t: "nav-coach-dashboard" },
        { to: "/exercises", icon: BookOpen, t: "nav-exercises-coach" },
        { to: "/videos", icon: PlayCircle, t: "nav-videos-coach" },
        { to: "/notifications", icon: Bell, t: NAV.notifications },
      ]
    : [
        { to: "/dashboard", icon: LayoutDashboard, t: NAV.dashboard },
        { to: "/workouts", icon: Dumbbell, t: NAV.workouts },
        { to: "/videos", icon: PlayCircle, t: "nav-videos" },
        { to: "/meals", icon: UtensilsCrossed, t: NAV.meals },
      ];

  useEffect(() => {
    let timer;
    const poll = async () => {
      try {
        const r = await api.get("/notifications");
        setUnread(r.data.filter((n) => !n.read).length);
      } catch {}
    };
    poll();
    timer = setInterval(poll, 30000);
    // Flush any workouts that were logged offline in a previous session.
    initOfflineSync();
    // Register the SW for everyone — offline caching shouldn't depend on
    // notification permission.
    registerServiceWorker();
    // Auto-start scheduler if user previously granted permission
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      startPushScheduler();
    }
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen flex bg-obsidian-900">
      {/* Sidebar */}
      <aside className="hidden md:flex md:flex-col w-60 border-r border-obsidian-600 bg-obsidian-900 sticky top-0 h-screen">
        <div className="px-5 py-6 border-b border-obsidian-600">
          <Link to={user?.is_coach ? "/coach-dashboard" : "/dashboard"} className="flex items-center gap-2 group">
            <img src="/icon-192.png" alt="Ripple Effect" className="h-7 w-7 rounded-sm" />
            <span className="font-heading text-2xl tracking-tight">RIPPLE<span className="text-blaze"> EFFECT</span></span>
          </Link>
          <div className="overline mt-2">{user?.is_coach ? "Coach console" : "v1.0 / Performance"}</div>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <Item key={item.to} {...item} />
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-obsidian-600">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-zinc-500 uppercase tracking-widest">Signed in</div>
            <div className="text-sm font-medium truncate">{user?.first_name || user?.email}</div>
            <div className="text-[11px] text-zinc-500 mt-1 uppercase tracking-wider">
              {user?.subscription?.status === "active"
                ? `${planLabel(user?.subscription?.plan)} • premium`
                : cancelledWithAccess(user)
                  ? `${planLabel(user?.subscription?.plan)} • cancelled`
                  : "Free tier"}
            </div>
          </div>
          <button
            onClick={logout}
            data-testid={NAV.logout}
            className="nav-item w-full text-left text-zinc-400 hover:text-blaze"
          >
            <LogOut className="h-4 w-4" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-obsidian-600 bg-obsidian-900 sticky top-0 z-10">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src="/icon-192.png" alt="Ripple Effect" className="h-6 w-6 rounded-sm" />
            <span className="font-heading text-xl">RIPPLE<span className="text-blaze"> EFFECT</span></span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              data-testid="nav-settings-mobile"
              onClick={() => navigate("/settings")}
              className="p-1.5 text-zinc-400 hover:text-blaze"
              aria-label="Settings"
            >
              <SettingsIcon className="h-5 w-5" />
            </button>
            <button
              data-testid={NAV.notifications + "-mobile"}
              onClick={() => navigate("/notifications")}
              className="relative p-1.5"
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5 text-zinc-400" />
              {unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-blaze text-white text-[9px] font-bold px-1.5 py-0.5 rounded-sm leading-none">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
            <button
              data-testid="nav-logout-mobile"
              onClick={logout}
              className="p-1.5 text-zinc-400 hover:text-blaze"
              aria-label="Logout"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="px-4 md:px-10 py-6 md:py-10 pb-24 md:pb-10 max-w-7xl mx-auto">
          {children}
        </div>
        {/* Mobile "More" drawer — role-aware, lists EVERY page so no feature
            is unreachable on phones. Sits above the bottom bar. */}
        {menuOpen && (
          <>
            <div
              data-testid="m-drawer-overlay"
              className="md:hidden fixed inset-0 bg-black/60"
              style={{ zIndex: 10001 }}
              onClick={() => setMenuOpen(false)}
            />
            <div
              data-testid="m-drawer"
              className="md:hidden fixed inset-x-0 bottom-0 bg-obsidian-900 border-t border-obsidian-600 rounded-t-xl max-h-[75vh] overflow-y-auto"
              style={{ zIndex: 10002, paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
            >
              <div className="flex items-center justify-between px-5 pt-4 pb-2">
                <span className="overline">{user?.is_coach ? "Coach menu" : "Menu"}</span>
                <button
                  data-testid="m-drawer-close"
                  onClick={() => setMenuOpen(false)}
                  className="p-1.5 text-zinc-400 hover:text-blaze"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 px-4 pb-2">
                {navItems.map(({ to, icon: Icon, label, testid, badge }) => (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={`m-drawer-${testid}`}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `relative flex flex-col items-center justify-center gap-1.5 rounded-md border py-3.5 px-1 text-[10px] uppercase tracking-wider text-center ${
                        isActive
                          ? "border-blaze/60 text-blaze bg-blaze/10"
                          : "border-obsidian-600 text-zinc-400"
                      }`
                    }
                  >
                    <Icon className="h-5 w-5" />
                    <span className="leading-tight">{label}</span>
                    {badge > 0 && (
                      <span className="absolute top-1.5 right-1.5 bg-blaze text-white text-[9px] font-bold px-1 py-0.5 rounded-sm leading-none">
                        {badge > 9 ? "9+" : badge}
                      </span>
                    )}
                  </NavLink>
                ))}
                <button
                  data-testid="m-drawer-logout"
                  onClick={logout}
                  className="flex flex-col items-center justify-center gap-1.5 rounded-md border border-obsidian-600 py-3.5 px-1 text-[10px] uppercase tracking-wider text-zinc-400 hover:text-blaze"
                >
                  <LogOut className="h-5 w-5" />
                  <span className="leading-tight">Logout</span>
                </button>
              </div>
            </div>
          </>
        )}

        {/* Bottom nav for mobile — z-[10000] sits ABOVE the platform's
            Emergent badge (z-index 9999) so tabs are always tappable.
            4 role-aware quick tabs + a "More" button opening the drawer. */}
        <div
          className="md:hidden fixed bottom-0 inset-x-0 grid grid-cols-5 bg-obsidian-900 border-t border-obsidian-600"
          style={{ zIndex: 10000, paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          {quickTabs.map(({ to, icon: Icon, t }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`m-${t}`}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-3 text-[10px] uppercase tracking-widest ${
                  isActive ? "text-blaze" : "text-zinc-500"
                }`
              }
            >
              <Icon className="h-5 w-5 mb-1" />
            </NavLink>
          ))}
          <button
            data-testid="m-nav-more"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="More"
            className={`flex flex-col items-center justify-center py-3 text-[10px] uppercase tracking-widest ${
              menuOpen ? "text-blaze" : "text-zinc-500"
            }`}
          >
            {menuOpen ? <X className="h-5 w-5 mb-1" /> : <Menu className="h-5 w-5 mb-1" />}
          </button>
        </div>
      </main>
    </div>
  );
}
